import { DEFAULT_PAGE_SIZE } from '@/lib/constants/pagination';
import { PaginatedResponse } from '@/modules/common';
import { ApiErrorCode, withApiErrorHandler } from '@/modules/common/api/errors';
import { mapMemberAccessListFromResponse } from '@/modules/settings/mappers/member-access.mapper';
import { axiosInstance } from '@/utils/axios';

import { MemberAccessRow } from '../types/member-access';
import {
  BulkInviteTeamMembersRequest,
  UpdateMemberUserGroupItem,
} from '../types/request/member-access-request';
import {
  BulkInviteTeamMembersResponse,
  TeamMemberUserGroupResponse,
} from '../types/response/member-access-response';

interface GetMemberAccessParams {
  cursor?: number;
  filters?: {
    search?: string;
    roleIds?: number[];
    statuses?: string[];
  };
}

/**
 * Fetches team members with their user group assignments
 * Supports filtering by search, roles, and statuses with cursor-based pagination
 */
export const getMemberAccess = async ({
  cursor,
  filters = {},
}: GetMemberAccessParams): Promise<PaginatedResponse<MemberAccessRow>> => {
  const endpoint = '/api/team/members/user-groups/search';

  const body: Record<string, any> = {};

  if (filters.search) {
    body.search = filters.search;
  }

  if (filters.roleIds && filters.roleIds.length > 0) {
    body.roleIds = filters.roleIds;
  }

  if (filters.statuses && filters.statuses.length > 0) {
    body.statuses = filters.statuses;
  }

  const apiData = await withApiErrorHandler(endpoint, ApiErrorCode.MEMBER_ACCESS_LOAD_FAILED, () =>
    axiosInstance.post<{
      data: {
        items: TeamMemberUserGroupResponse[];
        pagination: {
          total: number;
          cursor: number | null;
        };
      };
    }>(endpoint, body, { params: { pageSize: DEFAULT_PAGE_SIZE, cursor } })
  );

  return new PaginatedResponse({
    items: mapMemberAccessListFromResponse(apiData.items),
    pagination: {
      total: apiData.pagination.total,
      cursor: apiData.pagination.cursor,
    },
  });
};

/**
 * Updates team member user groups in batch
 * Accepts already-processed update items (comparison logic handled by caller)
 */
export const updateMemberAccess = async (
  teamMembers: UpdateMemberUserGroupItem[]
): Promise<void> => {
  const endpoint = '/api/team/members/user-groups';

  await withApiErrorHandler(endpoint, ApiErrorCode.MEMBER_ACCESS_UPDATE_FAILED, () =>
    axiosInstance.patch(endpoint, { teamMembers })
  );
};

export const bulkInviteTeamMembers = async (
  request: BulkInviteTeamMembersRequest
): Promise<BulkInviteTeamMembersResponse> => {
  const endpoint = '/api/team/members/invite/bulk';

  return withApiErrorHandler(endpoint, ApiErrorCode.BULK_INVITE_TEAM_MEMBERS_FAILED, () =>
    axiosInstance.post<{ data: BulkInviteTeamMembersResponse }>(endpoint, request)
  );
};
